import { useState } from 'react';

// our components
import ColumnItem from 'components/DataTable/ColumnItem';
import ColumnVerticalHeader from 'components/DataTable/ColumnVerticalHeader';
import ConfirmArchive from 'components/Modal/KeyActions/ConfirmKeyActionChange';
import DataTable from 'components/DataTable/DataTable';
import Loader from 'components/Loader';
import PreviewKeyAction from 'components/Modal/KeyActions/PreviewKeyAction';
import { SecondaryButton } from 'components/Button/Button';

// our hooks
import useGetKeyActions from 'hooks/keyActions/useGetKeyActions';
import useMutateChangeKeyActionStatus from 'hooks/keyActions/useMutateChangeKeyActionStatus';

// our utils
import formatKeyActionName from 'shared/utils/formatting/keyaction/formatKeyActionName';
import formatDate from 'shared/utils/date/formatDate';

function ArchivedKeyActions() {
	const [isConfirmArchiveOpen, setIsConfirmArchiveOpen] = useState(false);
	const [isKeyActionOpen, setIsKeyActionOpen] = useState(false);
	const [keyActionInfo, setKeyActionInfo] = useState(null);

	const toggleKeyAction = useMutateChangeKeyActionStatus();

	const {
		data: keyActions,
		isLoading: keyActionsLoading,
		isSuccess: keyActionsSuccess
	} = useGetKeyActions(
		(keyActionData) => {
			return keyActionData.map((keyAction) => ({
				...keyAction,
				clientFullName: `${keyAction.clientFirstName} ${keyAction.clientLastName}`,
				advisorFullName: `${keyAction.advisorFirstName} ${keyAction.advisorLastName}`,
				keyActionName: keyAction.type
			}));
		},
		{
			status: 'ARCHIVED'
		}
	);

	const clientFullNameKey = 'clientFullName';
	const tradelineIdKey = 'tradelineId';
	const keyActionNameKey = 'keyActionName';
	const advisorFullNameKey = 'advisorFullName';
	const advisorCompanyKey = 'advisorCompanyName';
	const dateQualifiedKey = 'qualifiedDate';

	const columnsAdvisors = [
		ColumnItem(clientFullNameKey, 'Client Full Name', 1, {
			renderHeader: ({ colDef }) => (
				<ColumnVerticalHeader headerName={colDef.headerName} />
			)
		}),
		ColumnItem(tradelineIdKey, 'Tradeline ID', 1, {
			renderHeader: ({ colDef }) => (
				<ColumnVerticalHeader headerName={colDef.headerName} />
			)
		}),
		ColumnItem(keyActionNameKey, 'Key Action Name', 1, {
			renderHeader: ({ colDef }) => (
				<ColumnVerticalHeader headerName={colDef.headerName} />
			),
			valueFormatter: ({ value }) => {
				return formatKeyActionName(value);
			}
		}),
		ColumnItem(dateQualifiedKey, 'Date Qualified', 1, {
			renderHeader: ({ colDef }) => (
				<ColumnVerticalHeader headerName={colDef.headerName} />
			),
			valueFormatter: ({ value }) => {
				return formatDate(value, 'MM/dd/yyyy');
			}
		}),
		ColumnItem(advisorFullNameKey, 'Advisor Full Name', 1, {
			renderHeader: ({ colDef }) => (
				<ColumnVerticalHeader headerName={colDef.headerName} />
			)
		}),
		ColumnItem(advisorCompanyKey, 'Advisor Company', 1, {
			renderHeader: ({ colDef }) => (
				<ColumnVerticalHeader headerName={colDef.headerName} />
			)
		}),
		ColumnItem('actions-2', 'Toggle Review', 1, {
			renderHeader: ({ colDef }) => (
				<ColumnVerticalHeader headerName={colDef.headerName} />
			),
			renderCell: ({ row }) => (
				<SecondaryButton
					onClick={(e) => {
						e.stopPropagation();
						setKeyActionInfo(row);
						setIsConfirmArchiveOpen(true);
					}}
				>
					Unarchive
				</SecondaryButton>
			),
			sortable: false
		})
	];

	if (keyActionsLoading) {
		return <Loader />;
	}

	if (keyActionsSuccess) {
		return (
			<>
				<DataTable
					columns={columnsAdvisors}
					rows={keyActions}
					disableSelectionOnClick
					rowsPerPageOptions={10}
					handleRowClick={(row) => {
						setKeyActionInfo(row);
						setIsKeyActionOpen(true);
					}}
				/>
				{isConfirmArchiveOpen && (
					<ConfirmArchive
						isOpen={isConfirmArchiveOpen}
						handleClose={() => setIsConfirmArchiveOpen(false)}
						keyActionInfo={keyActionInfo}
						title="Are you sure you want to unarchive this key action?"
						subtitle={`This will change the status to 'Review' for the following key action: ${keyActionInfo.keyActionName} for ${keyActionInfo.clientFirstName} ${keyActionInfo.clientLastName}.`}
						primaryButtonText="Unarchive action"
						confirmButtonCallback={() => {
							toggleKeyAction.mutate(
								{
									id: keyActionInfo.id,
									status: 'REVIEW'
								},
								{
									onSuccess: () => {
										setIsConfirmArchiveOpen(false);
									}
								}
							);
						}}
						isConfirmButtonLoading={toggleKeyAction.isLoading}
					/>
				)}
				{isKeyActionOpen && (
					<PreviewKeyAction
						keyActionInfo={keyActionInfo}
						isOpen={isKeyActionOpen}
						handleClose={() => setIsKeyActionOpen(false)}
					/>
				)}
			</>
		);
	}
}

export default ArchivedKeyActions;
